
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function BlogSection() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 });
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 });

  const featuredPost = {
    title: 'Ultimate Christmas Gift Shopping Guide 2025',
    excerpt: 'Everything you need to know about finding the perfect gifts this holiday season, from budget tips to luxury recommendations.',
    image: 'https://public.readdy.ai/ai/img_res/c10a1aeb1af68c4e149a263a92af35ca.jpg',
    category: 'Shopping Tips',
    readTime: '8 min read',
    author: 'Sarah Mitchell'
  };

  const posts = [
    {
      title: '10 Last-Minute Gift Ideas That Don\'t Look Last-Minute',
      image: 'https://readdy.ai/api/search-image?query=Quick%20elegant%20gift%20ideas%20with%20express%20delivery%20boxes%20and%20festive%20wrapping%20on%20clean%20surface%20with%20Christmas%20decorations%20professional%20lifestyle%20photography%20simple%20background&width=280&height=280&seq=blog-lastminute-001&orientation=squarish',
      category: 'Quick Tips',
      readTime: '5 min read'
    },
    {
      title: 'How to Choose Gifts for People Who Have Everything',
      image: 'https://readdy.ai/api/search-image?query=Unique%20creative%20luxury%20gifts%20and%20experiences%20displayed%20elegantly%20with%20festive%20Christmas%20decorations%20and%20sophisticated%20styling%20lifestyle%20photography%20simple%20background&width=280&height=280&seq=blog-unique-001&orientation=squarish',
      category: 'Gift Guides',
      readTime: '6 min read'
    },
    {
      title: 'Budget-Friendly Gifts Under $50 That Feel Expensive',
      image: 'https://readdy.ai/api/search-image?query=Affordable%20quality%20gifts%20arranged%20beautifully%20with%20price%20tags%20and%20Christmas%20decorations%20showing%20value%20and%20elegance%20product%20photography%20simple%20background&width=280&height=280&seq=blog-budget-001&orientation=squarish',
      category: 'Budget Tips',
      readTime: '7 min read'
    },
    {
      title: 'Sustainable Gift Wrapping Ideas for Eco-Conscious Shoppers',
      image: 'https://readdy.ai/api/search-image?query=Eco-friendly%20sustainable%20gift%20wrapping%20with%20natural%20materials%20reusable%20fabric%20and%20minimal%20Christmas%20decorations%20on%20wooden%20surface%20environmental%20lifestyle%20photography%20simple%20background&width=280&height=280&seq=blog-eco-001&orientation=squarish',
      category: 'Eco-Friendly',
      readTime: '5 min read'
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1B4332] mb-4">
            Gift Shopping Tips & Guides
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Expert advice to make your Christmas shopping easier and more enjoyable
          </p>
        </div>

        <div ref={contentRef} className="grid lg:grid-cols-12 gap-8 mb-12">
          <Link
            to="/reviews"
            className={`lg:col-span-7 group cursor-pointer transition-all duration-700 ${
              contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-full group-hover:shadow-[#D4AF37]/20 transition-shadow duration-500">
              <div className="w-full h-[500px] overflow-hidden">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent group-hover:from-black/95 transition-all duration-500"></div>
              <div className="absolute top-6 left-6">
                <span className="inline-block px-4 py-2 bg-[#D4AF37] text-white font-bold rounded-full text-sm animate-pulse-glow">
                  FEATURED
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12 transform group-hover:translate-y-[-8px] transition-transform duration-500">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full group-hover:bg-[#D4AF37]/80 transition-colors duration-300">
                    {featuredPost.category}
                  </span>
                  <span className="text-white/80 text-sm">{featuredPost.readTime}</span>
                </div>
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4 group-hover:text-[#D4AF37] transition-colors duration-300">
                  {featuredPost.title}
                </h3>
                <p className="text-white/90 text-lg mb-6 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#8B2635] rounded-full group-hover:scale-110 transition-transform duration-300"></div>
                  <div>
                    <p className="text-white font-semibold">{featuredPost.author}</p>
                    <p className="text-white/70 text-sm">Gift Expert</p>
                  </div>
                </div>
              </div>
              {/* Shimmer effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>
            </div>
          </Link>

          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
            {posts.slice(0, 2).map((post, index) => (
              <Link
                key={index}
                to="/reviews"
                className={`bg-white rounded-2xl overflow-hidden shadow-lg card-festive group transition-all duration-700 ${
                  contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
                }`}
                style={{ transitionDelay: `${(index + 1) * 150}ms` }}
              >
                <div className="w-full h-56 overflow-hidden relative">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-[#1B4332]/5 text-[#1B4332] text-xs font-semibold rounded-full group-hover:bg-[#1B4332]/10 transition-colors">
                      {post.category}
                    </span>
                    <span className="text-gray-500 text-xs">{post.readTime}</span>
                  </div>
                  <h4 className="font-bold text-[#1B4332] text-lg group-hover:text-[#8B2635] transition-colors leading-snug">
                    {post.title}
                  </h4>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {posts.slice(2).map((post, index) => (
            <Link
              key={index}
              to="/reviews"
              className={`bg-white rounded-2xl overflow-hidden shadow-lg card-festive group transition-all duration-700 ${
                contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${(index + 3) * 150}ms` }}
            >
              <div className="w-full h-56 overflow-hidden relative">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-[#1B4332]/5 text-[#1B4332] text-xs font-semibold rounded-full group-hover:bg-[#1B4332]/10 transition-colors">
                    {post.category}
                  </span>
                  <span className="text-gray-500 text-xs">{post.readTime}</span>
                </div>
                <h4 className="font-bold text-[#1B4332] group-hover:text-[#8B2635] transition-colors leading-snug">
                  {post.title}
                </h4>
              </div>
            </Link>
          ))}
        </div>

        <div 
          className={`text-center transition-all duration-700 delay-500 ${
            contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#1B4332] text-[#1B4332] rounded-full font-semibold hover:bg-[#1B4332] hover:text-white transition-all duration-300 whitespace-nowrap group"
          >
            View All Articles
            <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
